import {
  VOICE_RECORDING_STATUSES,
  type ResponderMessageAsset,
  type VoiceAssetCutInput,
  type VoiceRecordingFailure,
  type VoiceRecordingPort,
  type VoiceRecordingStatus,
} from '@/shared-responder-v2-core';
import { uploadFileToStorage } from '@/shared-utilities';

import { createMicrophoneLevelMeter } from './shared-responder-v2-microphone-meter';
import {
  buildVoiceAssetName,
  flushOpenChunk,
  pickVoiceMimeType,
  sliceVoiceChunks,
  supportsAudioRecording,
  toAudioConstraints,
  toUploadFailure,
  toVoiceRecordingFailure,
  toVoiceRecordingStatus,
  UNSUPPORTED_VOICE_FAILURE,
  VOICE_CHUNK_INTERVAL_MS,
  type SignedUploadRequest,
} from './shared-responder-v2-voice-recording.helpers';

interface RecordedChunk {
  readonly endedAtMs: number;
  readonly data: Blob;
}

type OpenedStreamListener = (stream: MediaStream) => void;

export type ResponderVoiceRecordingPort = VoiceRecordingPort & {
  /** Every capture this port opens, so a URL/Task recording borrows it rather than raising a second prompt. */
  readonly subscribeStream: (listener: OpenedStreamListener) => () => void;
};

/**
 * The reactive half of the port, republished as a value on every change. The port
 * is a stable object and the survey app builds with `reactCompiler: true`, so
 * anything derived from a getter on it was memoised on an identity that never
 * changes — a granted microphone kept reporting `requesting` to the gate.
 */
interface VoiceRecordingSnapshot {
  readonly status: VoiceRecordingStatus;
  readonly audioLevel: number;
  readonly failure: VoiceRecordingFailure | null;
}

export interface VoiceRecordingRuntime {
  readonly getSnapshot: () => VoiceRecordingSnapshot;
  readonly subscribe: (listener: () => void) => () => void;
  readonly actions: Omit<
    ResponderVoiceRecordingPort,
    keyof VoiceRecordingSnapshot
  >;
}

/**
 * One continuous microphone capture for a whole session, with a cut taken from
 * it per answer. Shared by the live responder and the builder preview: each app
 * supplies only its own schema's signed-upload call, so an author previewing
 * their project meets the same prompt and the same recording a respondent will.
 *
 * An anonymous session has no contact to attribute audio to, so `isAnonymous`
 * makes the answer travel as its transcript alone — the same rule the legacy
 * responder's `createMessageAsset` applied.
 */
export function createVoiceRecordingPort(
  requestSignedUpload: SignedUploadRequest,
  isAnonymous: boolean
): VoiceRecordingRuntime {
  let status: VoiceRecordingStatus = VOICE_RECORDING_STATUSES.Idle;
  let failure: VoiceRecordingFailure | null = null;
  let stream: MediaStream | null = null;
  let recorder: MediaRecorder | null = null;
  let mimeType = '';
  let startedAt = 0;
  let chunks: RecordedChunk[] = [];
  let audioLevel = 0;

  const listeners = new Set<() => void>();
  const streamListeners = new Set<OpenedStreamListener>();

  let snapshot: VoiceRecordingSnapshot = { status, audioLevel, failure };

  /**
   * Rebuilt only here, so `getSnapshot` returns the same object until something
   * changes — a fresh one per call is what makes `useSyncExternalStore` throw.
   */
  function notify(): void {
    snapshot = { status, audioLevel, failure };

    for (const listener of listeners) listener();
  }

  const sampleListeners = new Set<
    Parameters<NonNullable<VoiceRecordingPort['subscribeAudioSamples']>>[0]
  >();
  const levelMeter = createMicrophoneLevelMeter(
    (level) => {
      audioLevel = level;
      notify();
    },
    (sample) => {
      for (const listener of sampleListeners) listener(sample);
    }
  );

  /**
   * Bumped on every `release()` so a `getUserMedia` that resolves *after* the
   * port was released can tell. Without it the late resolve builds a recorder
   * and an analyser that nothing owns: in-session the lifecycle effect happens
   * to stop them, but past unmount the interval and the `AudioContext` leak for
   * the life of the tab.
   */
  let releaseSeq = 0;

  function settle(
    nextStatus: VoiceRecordingStatus,
    nextFailure: VoiceRecordingFailure | null
  ): void {
    status = nextStatus;
    failure = nextFailure;
    notify();
  }

  function release(): void {
    releaseSeq += 1;
    levelMeter.stop();

    if (recorder !== null && recorder.state !== 'inactive') recorder.stop();

    for (const track of stream?.getTracks() ?? []) track.stop();

    recorder = null;
    stream = null;
    chunks = [];
  }

  function fail(error: unknown): void {
    const nextFailure = toVoiceRecordingFailure(error);

    release();
    settle(toVoiceRecordingStatus(nextFailure), nextFailure);
  }

  async function start(deviceId?: string): Promise<void> {
    if (status === VOICE_RECORDING_STATUSES.Requesting) return;
    if (status === VOICE_RECORDING_STATUSES.Listening) return;

    const pickedMimeType = supportsAudioRecording()
      ? pickVoiceMimeType((candidate) =>
          MediaRecorder.isTypeSupported(candidate)
        )
      : null;

    if (pickedMimeType === null) {
      settle(VOICE_RECORDING_STATUSES.Unsupported, UNSUPPORTED_VOICE_FAILURE);
      return;
    }

    settle(VOICE_RECORDING_STATUSES.Requesting, null);

    const requestedAtSeq = releaseSeq;

    let opened: MediaStream;

    try {
      opened = await navigator.mediaDevices.getUserMedia(
        toAudioConstraints(deviceId)
      );
    } catch (error) {
      fail(error);

      return;
    }

    if (releaseSeq !== requestedAtSeq) {
      for (const track of opened.getTracks()) track.stop();

      return;
    }

    stream = opened;
    mimeType = pickedMimeType;
    startedAt = Date.now();
    chunks = [];

    /*
     * `MediaRecorder` and the meter's `AudioContext` both throw synchronously,
     * and `start` is called as `void start(...)`. An escaping throw here left
     * the port on `requesting` for the rest of the session — a microphone the
     * responder had already granted, reported as still being asked for.
     */
    try {
      const nextRecorder = new MediaRecorder(opened, {
        mimeType: pickedMimeType,
      });

      nextRecorder.addEventListener('dataavailable', (event) => {
        if (event.data.size > 0) {
          chunks.push({ endedAtMs: Date.now() - startedAt, data: event.data });
        }
      });

      recorder = nextRecorder;
      nextRecorder.start(VOICE_CHUNK_INTERVAL_MS);
      levelMeter.start(opened);
      settle(VOICE_RECORDING_STATUSES.Listening, null);
    } catch (error) {
      console.error('Voice recording failed to start', error);

      fail(error);

      return;
    }

    for (const listener of streamListeners) {
      try {
        listener(opened);
      } catch (error) {
        console.error('Voice recording stream listener failed', error);
      }
    }
  }

  function stop(): Promise<void> {
    release();
    settle(VOICE_RECORDING_STATUSES.Idle, null);

    return Promise.resolve();
  }

  async function cutAsset(
    input: VoiceAssetCutInput
  ): Promise<ResponderMessageAsset | null> {
    if (recorder === null || isAnonymous) {
      return null;
    }

    await flushOpenChunk(recorder);

    const sliced = sliceVoiceChunks(chunks, input.sinceMs ?? 0);

    if (sliced.length === 0) {
      return null;
    }

    const name = buildVoiceAssetName(mimeType, Date.now());
    const file = new File(
      sliced.map((entry) => entry.data),
      name,
      { type: mimeType }
    );

    try {
      const signed = await requestSignedUpload(name, mimeType);

      if (signed === null) return null;

      await uploadFileToStorage(signed.url, file);

      return { name, gcsKey: signed.path, mimetype: mimeType };
    } catch (error) {
      settle(status, toUploadFailure(error));

      return null;
    }
  }

  return {
    getSnapshot: () => snapshot,
    subscribe: (listener: () => void) => {
      listeners.add(listener);

      return () => listeners.delete(listener);
    },
    actions: {
      subscribeAudioSamples: (listener) => {
        sampleListeners.add(listener);
        return () => sampleListeners.delete(listener);
      },
      start,
      stop,
      cutAsset,
      subscribeStream: (listener) => {
        streamListeners.add(listener);

        if (status === VOICE_RECORDING_STATUSES.Listening && stream !== null) {
          listener(stream);
        }

        return () => streamListeners.delete(listener);
      },
    },
  };
}
